import {apiClient} from '@common/http/query-client';
import {setBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {Capacitor} from '@capacitor/core';
import {toast} from '@ui/toast/toast';
import {useEffect} from 'react';

type AppUrlOpenData = {url: string};

export function CapacitorAuthCallbackListener() {
  useEffect(() => {
    if (!isCapacitorApp()) return;

    let removeListener: undefined | (() => void);
    let cancelled = false;

    consumeLoginCodeFromCurrentUrl();

    import('@capacitor/app').then(({App}) => {
      if (cancelled) return;

      App.addListener('appUrlOpen', async (event: AppUrlOpenData) => {
        await handleAppUrlOpen(event.url);
      }).then(listener => {
        if (cancelled) {
          listener.remove();
        } else {
          removeListener = () => listener.remove();
        }
      });
    });

    return () => {
      cancelled = true;
      removeListener?.();
    };
  }, []);

  return null;
}

async function consumeLoginCodeFromCurrentUrl() {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('capacitorLoginCode');

  if (!code) return;

  url.searchParams.delete('capacitorLoginCode');
  window.history.replaceState(null, '', url.pathname + url.search + url.hash);

  await consumeLoginCode(code);
}

async function handleAppUrlOpen(url: string) {
  let parsedUrl: URL;

  try {
    parsedUrl = new URL(url);
  } catch {
    return;
  }

  if (parsedUrl.protocol !== 'trackdrop:' || parsedUrl.hostname !== 'auth') {
    return;
  }

  const code = parsedUrl.searchParams.get('code');
  if (!code) return;

  await consumeLoginCode(code);
}

async function consumeLoginCode(code: string) {
  try {
    const response = await apiClient.post('auth/capacitor-login-code', {code});
    const bootstrapData = response.data?.bootstrapData;

    if (bootstrapData) {
      setBootstrapData(bootstrapData);
    }

    window.location.replace('/');
  } catch (e) {
    toast.danger('Login expired. Please try connecting with MusicKraze again.');
  }
}

function isCapacitorApp(): boolean {
  return (
    Capacitor.isNativePlatform?.() ||
    navigator.userAgent.includes('TrackdropCapacitor')
  );
}
